import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useContext, useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';

import { msalConfig } from '@/utils/authConfig';
import { trackAuthRedirect } from '@/utils/authTelemetry';
import { AuthNavigationClient } from '@/utils/authNavigationClient';

import { TokenContext } from './TokenContext';
import { useMsalInitialization } from '@/hooks/useMsalInitialization';
import { useAuthEvents } from '@/hooks/useAuthEvents';

export const pca = new PublicClientApplication(msalConfig);

// Inactivity is the only reason the app signs a user out on its own.
const IDLE_LOGOUT_MS = 1000 * 60 * 60 * 12; // 12 hours

function AuthWrapper({ children }: { children: React.ReactNode }) {
  const { b2cId, updateAccessToken } = useContext(TokenContext);
  const navigate = useNavigate();
  const viteLogoutUrl = import.meta.env.VITE_REDIRECT_URL as string;

  // Initialize MSAL
  const {
    initialized,
    error: initError,
    authResult: redirectAuthResult,
  } = useMsalInitialization(pca);

  // Set token from redirect if we got an auth result
  useEffect(() => {
    if (redirectAuthResult != null) {
      updateAccessToken((prevValue: AuthenticationResult | null) => {
        if (prevValue?.accessToken === redirectAuthResult.accessToken) {
          return prevValue;
        }
        return redirectAuthResult;
      });
    }
  }, [redirectAuthResult, updateAccessToken]);

  // Handle auth events
  useAuthEvents(initialized ? pca : null, initialized);

  // Handle idle timeout (activity in any open tab counts)
  const onIdle = async () => {
    if (b2cId == null) return;

    const logoutRequest = {
      account: pca.getAccountByHomeId(b2cId),
      postLogoutRedirectUri: viteLogoutUrl,
    };
    trackAuthRedirect('Idle', { b2cId, idleTimeoutMs: IDLE_LOGOUT_MS });
    await pca.logoutRedirect(logoutRequest);
  };

  useIdleTimer({
    onIdle,
    timeout: IDLE_LOGOUT_MS,
    throttle: 500,
    crossTab: true,
  });

  // Setup navigation client
  useEffect(() => {
    const navigationClient = new AuthNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);
  }, [navigate]);

  if (initError != null) {
    return <div>Error initializing authentication</div>;
  }

  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}

export default AuthWrapper;
